// Civic pixel beacon. This module is bundled by `scripts/build.mjs` into a
// single self-executing browser asset published to `bryn-pixel-<env>/`. The
// exported functions exist so the behaviour is unit-testable without a DOM; the
// repo targets the ES2022 lib (no DOM types), so the browser surface is modelled
// with the minimal structural interfaces below and injected at the call site.

export type PixelPayload = {
  url: string;
  referrer: string;
  ts: number;
};

export type PixelConfig = {
  pixelRef: string;
  endpoint: string;
};

// Config supplied via a first-party `window.__brynPixel` global rather than the
// host `<script>` data attributes. This is the tag-manager path: a GTM custom
// template can inject `pixel.js` but cannot set `data-*` attributes on the
// injected script, so it publishes the ref (and optional endpoint) on this
// global instead. Both fields fall back behind their `data-*` equivalents.
export type PixelGlobalConfig = {
  ref?: string;
  endpoint?: string;
};

// The return leg (BRYN-594, BRYN-933, BRYN-942). The serve payload the beacon retrieves
// and surfaces inline: `status` drives the poll loop; `entity` carries company-level
// attributes + active Signal labels; `individual` carries the resolved person's
// attributes + their signals, or is null when no person resolved. Each section may
// include a stable identity-stitch key — `entity.entityId` (the resolved company's id)
// and `individual.individualId` (the resolved person's id) — for identity stitching
// (e.g. read them from `window.bryn`/`bryn:personalize` and call `posthog.identify()` /
// `posthog.group()`). Display attributes and `individual` arrive only with
// `status: "resolved"`, but `entity.entityId` / `entity.domain` travel as soon as the
// visitor's Entity exists — on a `"pending"` payload too (BRYN-1393) — so an unscored visit
// is still attributable to an account. Attributes are allowlist-filtered server-side.
//
// NOTE: intentionally duplicated from `apps/pipeline-ingest/src/serve/payload.ts`
// (`PixelServePayload`). The pixel is a standalone, import-free browser bundle
// (scripts/build.mjs transpiles this single module), so it can't share the server
// type. Keep the two shapes — and the OpenAPI schema in pixelServe.ts — in sync.
export type ServeStatus = "pending" | "resolved" | "suppressed";

// Attributes live at the section's top level (BRYN-933 flatten, decision 2026-07-01);
// `signals` is the one reserved key, every other key is a display attribute string.
export type ServeSection = {
  signals: string[];
  [attribute: string]: string | string[];
};

export type ServePayload = {
  status: ServeStatus;
  entity: ServeSection;
  individual: ServeSection | null;
  /**
   * Opaque correction handle (BRYN-1382). Not a display attribute and deliberately outside both DTO
   * sections — it is plumbing that must survive the round trip to the host page, because the
   * identity-stitch recipe stores this whole payload as an analytics person property and the
   * correction depends on reading it back. Dropping it here silently disables that mechanism end to
   * end, with nothing on either side reporting an error. Present only on a resolved payload.
   */
  linkId?: string;
};

type ScriptLike = {
  getAttribute(name: string): string | null;
};

type DocumentLike = {
  referrer: string;
  currentScript?: ScriptLike | null;
  // Fallback locator for the pixel's own <script> when `currentScript` is null (see
  // `findScript`). Optional so a host that wires neither is a silent no-op.
  querySelector?: (selector: string) => ScriptLike | null;
};

type LocationLike = {
  href: string;
};

type NavigatorLike = {
  sendBeacon?(url: string, data?: unknown): boolean;
};

type FetchInit = {
  method: string;
  headers: Record<string, string>;
  body: string;
  keepalive: boolean;
};

type FetchLike = (url: string, init: FetchInit) => unknown;
type BlobFactory = (body: string, type: string) => unknown;
type BlobCtor = new (parts: string[], options?: { type?: string }) => unknown;

// Return-leg (retrieve) browser surface, modelled structurally and injected at the
// call site like the publish surface above. All optional on PixelEnv: a host that
// wires none of them simply never retrieves (the publish leg is unaffected).
//
// The DOM personalization surface (`data-bryn-*`, below) reads an element's own marker
// attribute (`getAttribute`) and either sets its text (`textContent`) or toggles the
// standard `hidden` attribute (`set`/`removeAttribute`) — no untrusted value is ever
// interpolated into a selector or written as HTML.
type ElementLike = {
  textContent: string | null;
  getAttribute(name: string): string | null;
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
};
type ServeResponseLike = { ok: boolean; json(): Promise<unknown> };
type ServeFetchLike = (url: string) => Promise<ServeResponseLike>;
type StorageLike = { getItem(key: string): string | null; setItem(key: string, value: string): void };
type TimerLike = (handler: () => void, ms: number) => unknown;
type DispatchLike = (type: string, detail: unknown) => void;
type PublishLike = (payload: ServePayload) => void;
type QueryAllLike = (selector: string) => ArrayLike<ElementLike>;

export type PixelEnv = {
  document: DocumentLike;
  location: LocationLike;
  navigator?: NavigatorLike;
  fetch?: FetchLike;
  blob?: BlobFactory;
  now: () => number;
  // Return leg (optional):
  serveFetch?: ServeFetchLike;
  setTimeout?: TimerLike;
  storage?: StorageLike;
  dispatch?: DispatchLike;
  publish?: PublishLike;
  queryAll?: QueryAllLike;
};

type PixelGlobal = {
  document?: DocumentLike & { querySelectorAll?: (selector: string) => ArrayLike<ElementLike> };
  location?: LocationLike;
  navigator?: NavigatorLike;
  fetch?: FetchLike;
  Blob?: BlobCtor;
  setTimeout?: (handler: () => void, ms: number) => unknown;
  sessionStorage?: StorageLike;
  dispatchEvent?: (event: unknown) => boolean;
  CustomEvent?: new (type: string, init?: { detail?: unknown }) => unknown;
  // The most-recent applied serve payload, published for synchronous reads (decision 4a).
  bryn?: ServePayload;
  // First-party config global set by the tag-manager template (untrusted shape).
  __brynPixel?: unknown;
};

// Beacon path under the Civic origin. The pixel posts to `<base>/<pixelRef>`.
// The script is Civic-hosted, so when no explicit `data-endpoint` is given the
// base is derived from the host `<script src>` origin — a relative "/pixel"
// would resolve against the embedding customer page and silently lose every
// beacon, since the script runs in the customer's browser, not on Civic.
const PIXEL_PATH = "/pixel";
const ORIGIN_RE = /^(https?:\/\/[^/]+)/i;

const deriveDefaultBase = (script: ScriptLike): string | null => {
  const origin = script.getAttribute("src")?.match(ORIGIN_RE)?.[1];
  return origin ? `${origin}${PIXEL_PATH}` : null;
};

/**
 * Resolve the pixel's own `<script>` element. `document.currentScript` is the fast path
 * — it is set during a script's top-level run for a parser-inserted tag and, in
 * Blink/WebKit, for an async dynamically-injected one. Firefox reports null for an async
 * injected script, which is exactly the Google Tag Manager Custom HTML path: GTM strips
 * custom attributes off a pasted `<script>`, so the workaround appends the pixel tag in
 * JS instead (`data-bryn-pixel-ref` set via `setAttribute` survives). When `currentScript`
 * is null, fall back to finding that injected tag in the DOM — first by the ref attribute
 * it carries, then by the shared pixel `src` (for the tag-manager-template path where the
 * ref lives only on `window.__brynPixel`). Returns null when neither resolves (SSR or a
 * test import), which keeps {@link init} a silent no-op.
 */
export const findScript = (doc: DocumentLike): ScriptLike | null => {
  if (doc.currentScript) return doc.currentScript;
  const query = doc.querySelector;
  if (!query) return null;
  return (
    query.call(doc, "script[data-bryn-pixel-ref]") ??
    query.call(doc, `script[src*="${PIXEL_PATH}/pixel.js"]`) ??
    null
  );
};

/** Builds the beacon payload from the current page URL, referrer, and timestamp. */
export const buildPayload = (location: LocationLike, document: DocumentLike, now: number): PixelPayload => ({ url: location.href, referrer: document.referrer, ts: now });

/**
 * Resolves the pixel ref and ingest endpoint. The host `<script>` data
 * attributes win; a `window.__brynPixel` `fallback` (the tag-manager path) fills
 * each field when its attribute is absent. So: ref is `data-bryn-pixel-ref` else
 * `fallback.ref`; the base is `data-endpoint` else `fallback.endpoint` else the
 * script's own origin (`deriveDefaultBase`). Returns null — a silent no-op
 * rather than a thrown error in the page — when there is no script, no pixel
 * ref, or no absolute base (so a beacon is never posted to the embedding page's
 * origin).
 */
export const resolveConfig = (
  script: ScriptLike | null | undefined,
  fallback?: PixelGlobalConfig | null,
): PixelConfig | null => {
  if (!script) return null;
  const pixelRef = script.getAttribute("data-bryn-pixel-ref") ?? fallback?.ref;
  if (!pixelRef) return null;
  const base = script.getAttribute("data-endpoint") ?? fallback?.endpoint ?? deriveDefaultBase(script);
  if (!base) return null;
  const endpoint = `${stripTrailingSlashes(base)}/${encodeURIComponent(pixelRef)}`;
  return { pixelRef, endpoint };
};

// A scan instead of `/\/+$/`, whose backtracking is polynomial on adversarial input.
const stripTrailingSlashes = (value: string): string => {
  let end = value.length;
  while (end > 0 && value[end - 1] === "/") end -= 1;
  return value.slice(0, end);
};

/**
 * Prefers navigator.sendBeacon (survives page unload); falls back to a
 * keepalive fetch. Returns whether the payload was handed off to either.
 */
export const send = (env: PixelEnv, endpoint: string, payload: PixelPayload): boolean => {
  const body = JSON.stringify(payload);
  const beacon = env.navigator?.sendBeacon;
  if (beacon) {
    const data = env.blob ? env.blob(body, "application/json") : body;
    if (beacon.call(env.navigator, endpoint, data)) return true;
  }
  if (env.fetch) {
    env.fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
    return true;
  }
  return false;
};

// ---- Return leg (retrieve channel, BRYN-594) ----

/** The serve endpoint for a config: the publish endpoint plus `/serve`. */
export const retrieveUrl = (config: PixelConfig): string => `${config.endpoint}/serve`;

// Poll-with-backoff (decision 2026-06-23: same-session delivery; BRYN-1008: keep
// polling after resolution). Polling starts shortly after the first publish and backs
// off exponentially until it settles into a steady cadence (RETRIEVE_STEADY_MS). Until
// the first `resolved` response the loop is bounded by RETRIEVE_MAX_PENDING_ATTEMPTS, so
// a visitor that never resolves stops instead of polling for the whole tab lifetime: a
// visitor with no resolvable identity, a tenant with the return leg disabled (serves
// `pending` by design), or one still awaiting enrichment past the window — including one whose
// pending payload already carries the account stitch key (BRYN-1393), which is deliberately not
// treated as a resolution. The server only
// returns `resolved` once there is displayable data (BRYN-1008), so this window must cover
// the resolve+enrich latency; it is a deliberate trade-off (higher = catch slower first
// resolutions same-session, lower = less futile polling from the majority of visitors who
// never resolve). Once a `resolved` payload arrives the cap is lifted and polling continues
// indefinitely, so late-arriving enrichment and Signals keep flowing. It also stops on a
// terminal `suppressed` status or page unload.
const RETRIEVE_START_MS = 1000;
const RETRIEVE_BASE_MS = 1000;
const RETRIEVE_STEADY_MS = 20000;
// Cap on consecutive pre-resolution polls (~2.5 min at the steady cadence); lifted once a
// `resolved` payload is seen. Sized to cover resolve+enrich latency without unbounded
// polling from never-resolving visitors — tune with serve load / enrichment timing.
const RETRIEVE_MAX_PENDING_ATTEMPTS = 12;

/** Exponential backoff for poll attempt `n` (0-based), capped at `maxMs`. */
export const backoffDelay = (attempt: number, baseMs: number, maxMs: number): number =>
  Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt));

/** Plain-object guard (local, since the pixel bundle imports nothing). */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Narrow the untrusted `window.__brynPixel` global to a {@link PixelGlobalConfig},
 * keeping only string `ref`/`endpoint`. Returns undefined when it isn't a usable
 * object, so a bad value is simply ignored rather than breaking resolution.
 */
const readGlobalConfig = (value: unknown): PixelGlobalConfig | undefined => {
  if (!isRecord(value)) return undefined;
  const config: PixelGlobalConfig = {};
  if (typeof value.ref === "string") config.ref = value.ref;
  if (typeof value.endpoint === "string") config.endpoint = value.endpoint;
  return config;
};

/** A conservative guard for one bryn-path segment: a plain identifier, never a prototype key. */
const isSafePathSegment = (segment: string): boolean => /^[\w-]+$/.test(segment) && !FORBIDDEN_KEYS.has(segment);

// `suppressed` is the sole terminal status (BRYN-1008): the poll stops and caches on it.
// `resolved` is intentionally NOT terminal, so the pixel keeps polling for fresher
// entity/individual data; `pending` is the cold, keep-polling state.
const isTerminalStatus = (status: ServeStatus): boolean => status === "suppressed";

/**
 * Keys never copied from the (untrusted) serve JSON onto a plain object — a
 * prototype-pollution guard. The serve response is same-origin from Bryn, but the
 * beacon treats it defensively; these keys are never legitimate DTO attribute names.
 */
const FORBIDDEN_KEYS = new Set(["__proto__", "constructor", "prototype"]);

/** Narrow an unknown JSON value to a {@link ServeSection}, keeping only string attributes/signals. */
const parseSection = (raw: unknown): ServeSection => {
  const attributes: Record<string, string> = {};
  if (isRecord(raw)) {
    for (const key of Object.keys(raw)) {
      // `signals` is the reserved list key, not an attribute; FORBIDDEN_KEYS guards prototype pollution.
      if (key === "signals" || FORBIDDEN_KEYS.has(key)) continue;
      const value = raw[key];
      if (typeof value === "string") attributes[key] = value;
    }
  }
  const rawSignals = isRecord(raw) ? raw.signals : undefined;
  const signals = Array.isArray(rawSignals) ? rawSignals.filter((s): s is string => typeof s === "string") : [];
  return { ...attributes, signals };
};

/** Narrow an unknown JSON value to a {@link ServePayload}, or null when it isn't one. */
export const parseServePayload = (raw: unknown): ServePayload | null => {
  if (!isRecord(raw)) return null;
  if (raw.status !== "pending" && raw.status !== "resolved" && raw.status !== "suppressed") return null;
  const individual = isRecord(raw.individual) ? parseSection(raw.individual) : null;
  // `linkId` rides through unparsed into the published payload: the host page never renders it, but
  // the identity stitch forwards this object verbatim and the correction bridge reads it back.
  const linkId = typeof raw.linkId === "string" && raw.linkId.length > 0 ? { linkId: raw.linkId } : {};
  return { status: raw.status, entity: parseSection(raw.entity), individual, ...linkId };
};

// ---- DOM personalization via the `data-bryn-*` attribute vocabulary (BRYN-594) ----
//
// One learnable pattern rather than a documented attribute per field: every marker's
// value is a **bryn path** into the serve payload. The grammar (dot-separated):
//
//   [entity.|individual.]<attribute>     an attribute string  e.g. entity.companyName, firstName
//   [entity.|individual.]signals         the active-signal list for that level
//   [entity.|individual.]signals.<type>  membership — is <type> an active signal at that level
//   status                               the resolution status ("pending"|"resolved"|"suppressed")
//
// A bare (unprefixed) path searches both sections, individual first. Three attributes
// consume a path:
//
//   data-bryn-field="<path>"       set the element's text to the string the path resolves to
//   data-bryn-show="<condition>"   show the element only while the condition holds
//   data-bryn-hide="<condition>"   hide the element while the condition holds
//
// A <condition> is a bare path (present / non-empty / a true membership ⇒ true) or
// `<path>=<value>` (equality: a string match, or list membership). Show/hide toggle the
// standard `hidden` attribute, so the host authors the default state in markup and the
// beacon reveals or hides it once identity resolves (progressive enhancement).

const FIELD_ATTR = "data-bryn-field";
const SHOW_ATTR = "data-bryn-show";
const HIDE_ATTR = "data-bryn-hide";
// Search order for an unprefixed path: individual before entity, so the person wins any
// key collision with the company (matches the pre-BRYN-933 "individual renders last" rule).
const SEARCH_SECTIONS = ["individual", "entity"] as const;

/** Pick a named section off the payload; `individual` may be null. */
const sectionOf = (payload: ServePayload, name: string): ServeSection | null =>
  name === "entity" ? payload.entity : name === "individual" ? payload.individual : null;

/** A string display attribute (never the reserved `signals` list) off one section, or null. */
const attributeOf = (section: ServeSection, key: string): string | null => {
  if (key === "signals") return null;
  const value = section[key];
  return typeof value === "string" ? value : null;
};

// A resolved bryn path: a string attribute (`value`, also `status`), a signals list, or a
// signal-membership boolean. `null` from resolvePath means malformed / points at nothing.
type PathResult = { kind: "value"; value: string } | { kind: "list"; list: string[] } | { kind: "bool"; value: boolean };

/**
 * Resolve a bryn path against the payload. Reading (never writing) payload fields by key,
 * with the prototype-key guard on every segment, keeps a crafted path (`__proto__`, …)
 * from traversing object internals.
 */
export const resolvePath = (payload: ServePayload, path: string): PathResult | null => {
  const segments = path.split(".");
  if (!segments.every(isSafePathSegment)) return null;

  // Top-level `status` — a scalar, so only the bare `status` path.
  if (segments[0] === "status") return segments.length === 1 ? { kind: "value", value: payload.status } : null;

  // Optional section prefix; otherwise search both sections (individual first).
  const scoped = segments[0] === "entity" || segments[0] === "individual";
  const rest = scoped ? segments.slice(1) : segments;
  if (rest.length === 0 || rest.length > 2) return null;
  const names: readonly string[] = scoped ? [segments[0]] : SEARCH_SECTIONS;

  if (rest[0] === "signals") {
    if (rest.length === 2) {
      const type = rest[1];
      const present = names.some((n) => sectionOf(payload, n)?.signals.includes(type) ?? false);
      return { kind: "bool", value: present };
    }
    const list: string[] = [];
    for (const n of names) list.push(...(sectionOf(payload, n)?.signals ?? []));
    return { kind: "list", list };
  }

  if (rest.length !== 1) return null; // an attribute is a single trailing segment
  for (const n of names) {
    const section = sectionOf(payload, n);
    const value = section && attributeOf(section, rest[0]);
    if (value != null) return { kind: "value", value };
  }
  return null;
};

/** The string a `data-bryn-field` path resolves to, or null (then leave the host default). */
export const resolveFieldValue = (payload: ServePayload, path: string): string | null => {
  const result = resolvePath(payload, path);
  return result?.kind === "value" ? result.value : null;
};

/**
 * Evaluate a `data-bryn-show` / `data-bryn-hide` condition. A bare `<path>` is a presence
 * test (non-empty string, non-empty list, or a true membership); `<path>=<value>` is an
 * equality test (string match or list membership). Malformed / missing ⇒ false.
 */
export const evalCondition = (payload: ServePayload, condition: string): boolean => {
  const eq = condition.indexOf("=");
  const path = (eq === -1 ? condition : condition.slice(0, eq)).trim();
  const expected = eq === -1 ? null : condition.slice(eq + 1);
  const result = resolvePath(payload, path);
  if (!result) return false;
  if (expected === null) {
    if (result.kind === "value") return result.value.length > 0;
    if (result.kind === "list") return result.list.length > 0;
    return result.value;
  }
  if (result.kind === "value") return result.value === expected;
  if (result.kind === "list") return result.list.includes(expected);
  return result.value === (expected === "true");
};

/** Toggle the standard `hidden` attribute — XSS-free, CSS-overridable, no inline style. */
const setHidden = (el: ElementLike, hidden: boolean): void => {
  if (hidden) el.setAttribute("hidden", "");
  else el.removeAttribute("hidden");
};

/**
 * Apply `fn` to every element carrying `marker`, reading the marker's own value. Errors are
 * swallowed per element (one bad attribute never breaks the rest) and per family (an
 * unsupported/throwing `queryAll` never breaks the page).
 */
const eachMarked = (env: PixelEnv, marker: string, fn: (el: ElementLike, value: string) => void): void => {
  if (!env.queryAll) return;
  try {
    const nodes = env.queryAll(`[${marker}]`);
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      const value = el.getAttribute(marker);
      if (value == null) continue;
      try {
        fn(el, value);
      } catch {
        // One malformed marker must not break personalization for the others.
      }
    }
  } catch {
    // queryAll unsupported or threw — skip this family, never break the page.
  }
};

// The host's original field text, captured per element the first time we personalize it.
// Continuous polling (BRYN-1008) can apply a terminal `suppressed` after a `resolved` — the
// "stop showing / don't leak" path — or a later payload that drops a field. In both cases
// the field path stops resolving, and without the captured default we would leave stale
// personalized text on screen. Keyed by element identity, so it never mutates host markup.
const fieldDefaults = new WeakMap<ElementLike, string>();

/**
 * Apply a payload to the host DOM through the `data-bryn-*` vocabulary: inject field text,
 * then toggle `data-bryn-show` / `data-bryn-hide` elements. A no-op when the host wired no
 * `queryAll` (publisher-only installs).
 */
const applyDom = (env: PixelEnv, payload: ServePayload): void => {
  eachMarked(env, FIELD_ATTR, (el, path) => {
    const value = resolveFieldValue(payload, path);
    if (value != null) {
      // Capture the host default once, before the first overwrite, so it can be restored.
      if (!fieldDefaults.has(el)) fieldDefaults.set(el, el.textContent ?? "");
      el.textContent = value;
    } else {
      // No value resolves for this path (a suppressed payload, or a field dropped from a
      // later one): restore the host default rather than leaving stale personalized text.
      const original = fieldDefaults.get(el);
      if (original !== undefined) el.textContent = original;
    }
  });
  eachMarked(env, SHOW_ATTR, (el, condition) => setHidden(el, !evalCondition(payload, condition)));
  eachMarked(env, HIDE_ATTR, (el, condition) => setHidden(el, evalCondition(payload, condition)));
};

/**
 * Surface a retrieved payload three ways (spec §17.4): apply it to the DOM via the
 * `data-bryn-*` attribute vocabulary (field text + show/hide — see `applyDom`), publish
 * the latest payload to the `window.bryn` DOM global for synchronous reads (decision 4a),
 * and dispatch a `bryn:personalize` CustomEvent (observer mode). All three fire on every
 * applied payload — the observer and global paths are what let the page upgrade after
 * load (decision 2026-06-23).
 */
export const applyServePayload = (env: PixelEnv, payload: ServePayload): void => {
  applyDom(env, payload);
  // Best-effort: a host page that already defines `window.bryn` as a non-writable or
  // accessor property would throw on assignment. Isolate the new sink so a global-name
  // collision can't break the event path below or the poll/cache flow in startRetrieve.
  try {
    env.publish?.(payload);
  } catch {
    // Non-fatal — the render and event surfaces still fire.
  }
  if (env.dispatch) env.dispatch("bryn:personalize", payload);
};

/** First-party session cache key for a serve URL, so later pages don't re-retrieve. */
const cacheKey = (serveUrl: string): string => `bryn:serve:${serveUrl}`;

/**
 * Begin the return leg: apply a cached `suppressed` resolution if present, else poll the
 * serve endpoint with backoff that settles into a steady cadence and keeps polling
 * (BRYN-1008), so late-arriving enrichment and Signals continue to flow. Each response is
 * applied (the page shows the library default first, then upgrades on every poll); only a
 * terminal `suppressed` payload is cached and stops the loop. A `resolved` payload is
 * applied but never stops polling. A no-op when the host wired neither `serveFetch` nor
 * `setTimeout`.
 */
export const startRetrieve = (env: PixelEnv, serveUrl: string): void => {
  if (!env.serveFetch || !env.setTimeout) return;
  const fetchServe = env.serveFetch;
  const schedule = env.setTimeout;

  // Apply a payload only when it differs from the last one applied (BRYN-1008). The poll
  // now runs for the tab's lifetime, so re-applying an unchanged payload would re-fire
  // `bryn:personalize` (and its listener side effects, e.g. posthog.identify/group) and
  // rewrite the DOM every cycle. Tracking the last-applied JSON keeps continuous polling
  // cheap and side-effect-free until the served data actually changes.
  let lastAppliedJson: string | undefined;
  const applyIfChanged = (payload: ServePayload): void => {
    const json = JSON.stringify(payload);
    if (json === lastAppliedJson) return;
    lastAppliedJson = json;
    applyServePayload(env, payload);
  };

  // Apply a cached terminal (suppressed) payload immediately and skip polling entirely.
  const cached = env.storage?.getItem(cacheKey(serveUrl));
  if (cached) {
    try {
      const payload = parseServePayload(JSON.parse(cached));
      if (payload && isTerminalStatus(payload.status)) {
        applyIfChanged(payload);
        return;
      }
    } catch {
      // Corrupt cache entry — ignore and poll fresh.
    }
  }

  // Bounded until the first resolution, then unbounded (BRYN-1008). A visitor that only
  // ever gets `pending` (a cold visitor who never resolves, or a return-leg-disabled tenant
  // that serves `pending` by design) stops after RETRIEVE_MAX_PENDING_ATTEMPTS rather than
  // polling for the tab's lifetime and burning that tenant's serve/rate-limit budget. Once
  // `resolvedSeen` flips, the cap is lifted so the pixel keeps polling at the steady cadence
  // for fresher entity/individual data.
  let resolvedSeen = false;
  const scheduleNext = (n: number): void => {
    if (!resolvedSeen && n + 1 >= RETRIEVE_MAX_PENDING_ATTEMPTS) return;
    schedule(() => attempt(n + 1), backoffDelay(n, RETRIEVE_BASE_MS, RETRIEVE_STEADY_MS));
  };

  const attempt = (n: number): void => {
    fetchServe(serveUrl)
      .then((res) => (res.ok ? res.json() : Promise.resolve(null)))
      .then((raw) => {
        const payload = parseServePayload(raw);
        if (payload) {
          // Once resolved, ignore a transient `pending` (a serve-error fallback or an
          // expired/absent identity lookup): applying it would downgrade window.bryn and
          // the `bryn:personalize` event to cold and re-hide `status=resolved` elements,
          // while the already-written `data-bryn-field` text stays put, leaving the page
          // in a mixed state. Keep the personalized surfaces and keep polling for fresher
          // resolved data instead. A real "stop showing" is the terminal `suppressed`.
          if (!(resolvedSeen && payload.status === "pending")) applyIfChanged(payload);
          if (payload.status === "resolved") resolvedSeen = true;
        }
        if (payload && isTerminalStatus(payload.status)) {
          try {
            env.storage?.setItem(cacheKey(serveUrl), JSON.stringify(payload));
          } catch {
            // Storage unavailable/full — non-fatal; the payload is already applied.
          }
          return; // terminal (suppressed): stop polling, keep the cached payload
        }
        scheduleNext(n);
      })
      .catch(() => {
        // Transient failure: keep polling at the steady cadence, never break the page.
        scheduleNext(n);
      });
  };

  schedule(() => attempt(0), RETRIEVE_START_MS);
};

/**
 * Resolves config from the host script, sends one publish beacon, then arms the
 * return leg (BRYN-594): polls the serve endpoint and surfaces the payload inline.
 */
export const init = (env: PixelEnv, fallback?: PixelGlobalConfig | null): boolean => {
  const config = resolveConfig(findScript(env.document), fallback);
  if (!config) return false;
  const sent = send(env, config.endpoint, buildPayload(env.location, env.document, env.now()));
  startRetrieve(env, retrieveUrl(config));
  return sent;
};

/**
 * Browser entry point: wires the ambient globals into {@link init}. A no-op when
 * there is no document or location (e.g. server-side rendering or a test import);
 * with a document but no resolvable pixel `<script>` (see {@link findScript}), init
 * itself no-ops.
 */
export const autostart = (global: PixelGlobal): boolean => {
  if (!global.document || !global.location) return false;
  const BlobImpl = global.Blob;
  const doc = global.document;

  // Return-leg wiring (all optional — a missing global just disables that capability).
  const rawFetch = global.fetch as unknown as
    | ((url: string, init?: Record<string, unknown>) => Promise<ServeResponseLike>)
    | undefined;
  const serveFetch: ServeFetchLike | undefined = rawFetch
    ? (url) => rawFetch(url, { method: "GET", credentials: "include", mode: "cors" })
    : undefined;
  const CustomEventImpl = global.CustomEvent;
  const dispatchEvent = global.dispatchEvent;
  const dispatch: DispatchLike | undefined =
    CustomEventImpl && dispatchEvent
      ? (type, detail) => {
          dispatchEvent.call(global, new CustomEventImpl(type, { detail }));
        }
      : undefined;
  const queryAll: QueryAllLike | undefined = doc.querySelectorAll
    ? (selector) => doc.querySelectorAll!(selector)
    : undefined;
  // Publish the latest payload to `window.bryn` for synchronous reads (decision 4a) —
  // a customer can read `window.bryn` without registering a `bryn:personalize` listener.
  const publish: PublishLike = (payload) => {
    global.bryn = payload;
  };

  return init({
    document: global.document,
    location: global.location,
    navigator: global.navigator,
    fetch: global.fetch,
    blob: BlobImpl ? (body, type) => new BlobImpl([body], { type }) : undefined,
    now: () => Date.now(),
    serveFetch,
    setTimeout: global.setTimeout ? (handler, ms) => global.setTimeout!(handler, ms) : undefined,
    storage: global.sessionStorage,
    dispatch,
    publish,
    queryAll,
  }, readGlobalConfig(global.__brynPixel));
};

autostart(globalThis as unknown as PixelGlobal);
